import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	BlockStack,
	Icon,
	InlineStack,
	Tag,
	Text,
	TextField,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";

interface CatalogMultiSelectProps {
	label: string;
	helpText?: string;
	placeholder?: string;
	/** Available catalog values. Pass `null` to indicate the catalog API failed — the component will fall back to a plain TextField. */
	options: string[] | null;
	/** Whether catalog values are still loading. */
	loading?: boolean;
	/** Currently selected values. Always an array, even in single-select mode (length 0 or 1). */
	selected: string[];
	onChange: (next: string[]) => void;
	multiple?: boolean;
	disabled?: boolean;
}

export function CatalogMultiSelect({
	label,
	helpText,
	placeholder,
	options,
	loading = false,
	selected,
	onChange,
	multiple = false,
	disabled = false,
}: CatalogMultiSelectProps) {
	const [input, setInput] = useState("");

	// Fallback: catalog unavailable. Render a plain TextField users can type into.
	if (options === null) {
		const asCsv = selected.join(", ");
		return (
			<TextField
				label={label}
				value={asCsv}
				onChange={(v) => {
					const parts = multiple
						? v
								.split(",")
								.map((s) => s.trim())
								.filter(Boolean)
						: v
							? [v.trim()]
							: [];
					onChange(parts);
				}}
				autoComplete="off"
				placeholder={placeholder}
				helpText={
					helpText
						? `${helpText} (Catalog unavailable — type values manually${
								multiple ? ", separated by commas" : ""
							}.)`
						: undefined
				}
				disabled={disabled}
			/>
		);
	}

	const availableOptions = useMemo(
		() => options.map((value) => ({ value, label: value })),
		[options],
	);

	const visibleOptions = useMemo(() => {
		if (!input.trim()) return availableOptions;
		const needle = input.trim().toLowerCase();
		return availableOptions.filter(({ label: optionLabel }) =>
			optionLabel.toLowerCase().includes(needle),
		);
	}, [availableOptions, input]);

	const handleSelect = useCallback(
		(next: string[]) => {
			if (multiple) {
				onChange(next);
			} else {
				onChange(next.length > 0 ? [next[next.length - 1]] : []);
			}
			setInput("");
		},
		[multiple, onChange],
	);

	const removeTag = useCallback(
		(value: string) => onChange(selected.filter((v) => v !== value)),
		[onChange, selected],
	);

	const textField = (
		<Autocomplete.TextField
			label={label}
			value={input}
			onChange={setInput}
			prefix={<Icon source={SearchIcon} tone="base" />}
			placeholder={
				loading
					? "Loading catalog…"
					: (placeholder ?? (multiple ? "Search and add…" : "Search…"))
			}
			autoComplete="off"
			helpText={helpText}
			disabled={disabled || loading}
		/>
	);

	return (
		<BlockStack gap="200">
			<Autocomplete
				allowMultiple={multiple}
				options={visibleOptions}
				selected={selected}
				onSelect={handleSelect}
				textField={textField}
				loading={loading}
				emptyState={
					<div style={{ padding: "0.75rem" }}>
						<Text as="p" variant="bodyMd" tone="subdued">
							{availableOptions.length === 0
								? "No values in your catalog yet."
								: "No matches."}
						</Text>
					</div>
				}
			/>
			{selected.length > 0 && (
				<InlineStack gap="100" wrap>
					{selected.map((value) => (
						<Tag key={value} onRemove={disabled ? undefined : () => removeTag(value)}>
							{value}
						</Tag>
					))}
				</InlineStack>
			)}
		</BlockStack>
	);
}

/**
 * Small helper hook: fetches `/api/catalog-facets` once. Returns vendors/productTypes arrays
 * (null while the call is in flight; null-valued props on failure signal "show fallback").
 */
export function useCatalogFacets(
	fetcher: () => Promise<{ vendors: string[]; productTypes: string[] }>,
) {
	const [vendors, setVendors] = useState<string[] | null>(null);
	const [productTypes, setProductTypes] = useState<string[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let cancelled = false;
		fetcher()
			.then((data) => {
				if (cancelled) return;
				setVendors(data.vendors);
				setProductTypes(data.productTypes);
			})
			.catch(() => {
				if (cancelled) return;
				setFailed(true);
				setVendors(null);
				setProductTypes(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [fetcher]);

	return { vendors, productTypes, loading, failed };
}
